import { c as _c } from "react/compiler-runtime";
import Badge, { type BadgeColor } from '@precis-digital/kurama/Badge';
import { type ButtonProps } from '@precis-digital/kurama/Button';
import Grid from '@precis-digital/kurama/Grid';
import MultiSelectDropdown, { MultiSelectButtonOpener, type MultiSelectDropdownItem } from '@precis-digital/kurama/MultiSelectDropdown';
import { styled } from '@precis-digital/kurama/utils';
import { type ThemeColor } from '@precis-digital/kurama/theme';
import Tooltip from '@precis-digital/kurama/Tooltip';
import { type Dispatch, memo, type ReactElement } from 'react';
import AlertSymbol from 'shared/components/Alert/AlertSymbol';
import AlertTooltip, { type TooltipPropsWithAlert } from 'shared/components/Alert/AlertTooltip';
import type { ChartCardMetricChoiceType, ChartCardStateAction, ChartChoice, ChartDimensionValuesChoice, ChartDimensionValuesOfChoice, ChartMetricsChoice, ChartVariantValuesChoice } from '../internalTypes';
import type { ChartCardTFunction, ChartChoiceOptions, ChartDataRow, ChartType, LinePattern, MultipleSelectionValue, SingleSelectionValue } from '../types';
import { assertNever, errorLocal, generateIdForSelectionOthers, getLabelOrValueAsLabel, isSelectionOthersId, singleToPlural } from '../utils/generics';
import { changeIsVisible } from '../utils/changeVisibility';
import ChartChip from './ChartChip';
export type MultipleSelectionChoiceProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> = {
  type: ChartCardMetricChoiceType;
  choice: ChartMetricsChoice<DataKey, DataRow>;
  parentChoice?: never;
  sortedValues?: never;
  selected: MultipleSelectionValue<string>;
  selectedOthers?: never;
  otherSelected?: never;
} | {
  type: ChartVariantValuesChoice<DataKey, DataRow>['type'];
  choice: ChartVariantValuesChoice<DataKey, DataRow>;
  parentChoice?: ChartChoice<DataKey, DataRow>;
  sortedValues?: never;
  selected: MultipleSelectionValue<string>;
  selectedOthers?: never;
  otherSelected?: never;
} | {
  type: ChartDimensionValuesChoice<DataKey, DataRow>['type'];
  choice: ChartDimensionValuesChoice<DataKey, DataRow>;
  parentChoice?: ChartChoice<DataKey, DataRow>;
  sortedValues?: string[];
  selected: MultipleSelectionValue<string>;
  selectedOthers: boolean;
  otherSelected?: never;
} | {
  type: ChartDimensionValuesOfChoice<DataKey, DataRow>['type'];
  choice: ChartDimensionValuesOfChoice<DataKey, DataRow>;
  parentChoice?: ChartChoice<DataKey, DataRow>;
  sortedValues?: string[];
  selected: MultipleSelectionValue<string>;
  selectedOthers: boolean;
  otherSelected: SingleSelectionValue<string>;
};
type MultipleSelectionProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> = MultipleSelectionChoiceProps<DataKey, DataRow> & {
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
  useChips: boolean;
  locked?: boolean;
  t: ChartCardTFunction;
};
const getChoiceOptions = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(props: MultipleSelectionChoiceProps<DataKey, DataRow>): ChartChoiceOptions<string> | ChartChoiceOptions<DataKey> => {
  const {
    type,
    choice
  } = props;
  if (type === 'valuesOf') {
    const {
      otherSelected
    } = props;
    return choice.change.options[otherSelected as DataKey];
  }
  return choice.change.options;
};
const useChipsElements = props => {
  const $ = _c(31);
  const {
    type,
    choice,
    parentChoice,
    sortedValues,
    selected,
    selectedOthers,
    dispatch,
    useChips,
    locked: t0,
    t
  } = props;
  const locked = t0 === undefined ? false : t0;
  if (!useChips) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = [];
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const {
    id,
    chartType,
    color: choiceColor,
    pattern: choicePattern,
    description
  } = choice;
  let chipsElements;
  if ($[1] !== chartType || $[2] !== choice || $[3] !== choiceColor || $[4] !== choicePattern || $[5] !== dispatch || $[6] !== id || $[7] !== locked || $[8] !== parentChoice || $[9] !== props || $[10] !== selected || $[11] !== selectedOthers || $[12] !== sortedValues || $[13] !== t || $[14] !== type) {
    const {
      color: parentChoiceColor,
      pattern: parentChoicePattern
    } = parentChoice ?? {};
    const label = choice.label ?? id;
    const labelPlural = singleToPlural(label, 2);
    const options = getChoiceOptions(props);
    let t1;
    if ($[16] !== choice || $[17] !== dispatch || $[18] !== props.otherSelected || $[19] !== selected) {
      t1 = value => {
        const allValues = selected.filter(v => v !== value);
        handleChange(choice, allValues, true, props.otherSelected, dispatch, [value]);
      };
      $[16] = choice;
      $[17] = dispatch;
      $[18] = props.otherSelected;
      $[19] = selected;
      $[20] = t1;
    } else {
      t1 = $[20];
    }
    const removeHandler = t1;
    let t2;
    if ($[21] !== choice || $[22] !== dispatch || $[23] !== props.otherSelected || $[24] !== selected) {
      t2 = () => {
        const allValues_0 = selected.filter(_temp);
        handleChange(choice, allValues_0, true, props.otherSelected, dispatch, []);
      };
      $[21] = choice;
      $[22] = dispatch;
      $[23] = props.otherSelected;
      $[24] = selected;
      $[25] = t2;
    } else {
      t2 = $[25];
    }
    const removeOthersHandler = t2;
    chipsElements = [];
    for (const selectedValue of sortedValues ?? selected) {
      if (!selected.includes(selectedValue)) {
        continue;
      }
      const option = options.find(t3 => {
        const {
          value: value_0
        } = t3;
        return value_0 === selectedValue;
      });
      if (option == null) {
        errorLocal(`MultipleSelection ${id} ${type} invalid option`, {
          options,
          selectedValue
        });
        continue;
      }
      const {
        value: value_1,
        color,
        label: label_0,
        description: description_0,
        pattern,
        alert,
        alertText
      } = option;
      chipsElements.push(createChipElement({
        chartType,
        value: value_1,
        label: label_0,
        color: color ?? choiceColor ?? parentChoiceColor,
        pattern: pattern ?? choicePattern ?? parentChoicePattern,
        alert,
        alertText,
        description: description_0,
        locked,
        removeHandler
      }));
    }
    if (selectedOthers === true) {
      let t3;
      if ($[26] !== selected) {
        t3 = t4 => {
          const {
            value: value_2
          } = t4;
          return !selected.includes(value_2);
        };
        $[26] = selected;
        $[27] = t3;
      } else {
        t3 = $[27];
      }
      const otherOptions = options.filter(t3);
      const {
        color: color_0,
        pattern: pattern_0,
        alert: alert_0,
        alertText: alertText_0
      } = otherOptions[0];
      const descriptionBody = <Grid container={true} flexDirection="column">{otherOptions.filter(_temp2).map(_temp3)}</Grid>;
      chipsElements.push(createChipElement({
        chartType,
        value: generateIdForSelectionOthers(id),
        label: t("others"),
        color: color_0 ?? choiceColor ?? parentChoiceColor,
        pattern: pattern_0 ?? choicePattern ?? parentChoicePattern,
        alert: alert_0,
        alertText: alertText_0,
        description: t("othersTitle", {
          label: label.toLowerCase(),
          labels: labelPlural.toLocaleLowerCase()
        }),
        descriptionBody,
        locked,
        removeHandler: removeOthersHandler
      }));
    }
    $[1] = chartType;
    $[2] = choice;
    $[3] = choiceColor;
    $[4] = choicePattern;
    $[5] = dispatch;
    $[6] = id;
    $[7] = locked;
    $[8] = parentChoice;
    $[9] = props;
    $[10] = selected;
    $[11] = selectedOthers;
    $[12] = sortedValues;
    $[13] = t;
    $[14] = type;
    $[15] = chipsElements;
  } else {
    chipsElements = $[15];
  }
  if (description == null) {
    return chipsElements;
  }
  let t1;
  if ($[28] !== chipsElements || $[29] !== description) {
    t1 = <Tooltip kind="singleline" title={description} placement="bottom-end">{chipsElements}</Tooltip>;
    $[28] = chipsElements;
    $[29] = description;
    $[30] = t1;
  } else {
    t1 = $[30];
  }
  return t1;
};
const MultipleSelection = props => {
  const $ = _c(10);
  const {
    choice,
    useCustomSelectElement,
    useChips,
    locked: t0
  } = props;
  const locked = t0 === undefined ? false : t0;
  const chipsElements = useChipsElements(props);
  if (!changeIsVisible(choice)) {
    return null;
  }
  if (useChips) {
    let t1;
    if ($[0] !== locked || $[1] !== props || $[2] !== useCustomSelectElement) {
      t1 = useCustomSelectElement === true || locked ? undefined : <SelectChipsElement {...props} />;
      $[0] = locked;
      $[1] = props;
      $[2] = useCustomSelectElement;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    const selectChipsElement = t1;
    const t2 = !locked;
    const t3 = !locked && selectChipsElement;
    let t4;
    if ($[4] !== chipsElements || $[5] !== t2 || $[6] !== t3) {
      t4 = <StyledChipsGrid container={true} editable={t2}>{chipsElements}{t3}</StyledChipsGrid>;
      $[4] = chipsElements;
      $[5] = t2;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t1;
  if ($[8] !== props) {
    t1 = <SelectChipsElement {...props} />;
    $[8] = props;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
};
interface SelectChipsCustomProps {
  options: MultiSelectDropdownItem[];
  value: string[];
  useChips: boolean;
  variant: ButtonProps['variant'];
  choiceId: string;
  choiceLabel: string | undefined;
  choiceDescription: string | undefined;
  disabled?: boolean;
  t: ChartCardTFunction;
}
const SelectChipsButton = t0 => {
  const $ = _c(25);
  let T0;
  let choiceDescription;
  let disabled;
  let isDropdownOpen;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== t0) {
    const {
      onClick,
      isDropdownOpen: t7,
      ...propsWithCustomProps
    } = t0;
    isDropdownOpen = t7;
    const {
      options,
      value,
      useChips,
      variant,
      choiceId,
      choiceLabel,
      choiceDescription: t8,
      disabled: t9,
      t,
      ...t10
    } = propsWithCustomProps as SelectChipsCustomProps;
    choiceDescription = t8;
    disabled = t9;
    props = t10;
    T0 = MultiSelectButtonOpener;
    t1 = `${choiceId}-add`;
    t2 = options;
    t3 = value;
    t4 = isDropdownOpen;
    t5 = onClick;
    t6 = choiceLabel == null ? t("select") : `${t("select")} ${singleToPlural(choiceLabel, 2).toLowerCase()}`;
    $[0] = t0;
    $[1] = T0;
    $[2] = choiceDescription;
    $[3] = disabled;
    $[4] = isDropdownOpen;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    T0 = $[1];
    choiceDescription = $[2];
    disabled = $[3];
    isDropdownOpen = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
  }
  const t7 = isDropdownOpen || (disabled ?? false);
  let t8;
  if ($[12] !== T0 || $[13] !== props || $[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t8 = <T0 key={t1} options={t2} value={t3} isDropdownOpen={t4} onClick={t5} buttonTitle={t6} {...props} disabled={t7} />;
    $[12] = T0;
    $[13] = props;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const button = t8;
  if (choiceDescription == null) {
    return button;
  }
  let t9;
  if ($[22] !== button || $[23] !== choiceDescription) {
    t9 = <Tooltip kind="singleline" title={choiceDescription} placement="bottom-end">{button}</Tooltip>;
    $[22] = button;
    $[23] = choiceDescription;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
};
const getItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  type,
  selected,
  limitReached,
  addOthers,
  options,
  label,
  id,
  t
}: {
  type: MultipleSelectionProps<DataKey, DataRow>['type'];
  selected: MultipleSelectionValue<string>;
  limitReached: boolean;
  addOthers: boolean;
  options: ChartChoiceOptions<string> | ChartChoiceOptions<DataKey>;
  label: string;
  id: string;
  t: ChartCardTFunction;
}): MultiSelectDropdownItem[] => {
  const items: MultiSelectDropdownItem[] = [];
  const labelPlural = singleToPlural(label, 2).toLowerCase();
  if (type === 'values' || type === 'valuesOf') {
    items.push({
      value: generateIdForSelectionOthers(id),
      label: t('selectOthers'),
      tooltipProps: {
        kind: 'singleline',
        title: t('selectOthersTitle', {
          label: label.toLowerCase(),
          labels: labelPlural.toLowerCase()
        })
      },
      disabled: !addOthers
    });
  }
  for (const {
    value,
    label,
    description,
    color,
    alert,
    alertText
  } of options) {
    items.push({
      value,
      label: getLabelOrValueAsLabel(label, value),
      tooltipProps: description == null ? undefined : {
        kind: 'singleline',
        title: description
      },
      icon: color == null ? undefined : <Badge color={color as BadgeColor} variant="lighter" />,
      rightIcon: <AlertTooltip alert={alert} alertText={alertText} key="alert" />,
      disabled: limitReached && !selected.includes(value)
    });
  }
  return items;
};
const getValuesAndOthers = (allValues: string[], canHaveOthers: boolean): {
  values: string[];
  others: boolean;
} => {
  const values = allValues.filter(value => !isSelectionOthersId(value));
  const others = canHaveOthers && allValues.some(value => isSelectionOthersId(value));
  return {
    values,
    others
  };
};
const handleChange = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(choice: MultipleSelectionProps<DataKey, DataRow>['choice'], allValues: string[], canHaveOthers: boolean, otherSelected: SingleSelectionValue<string> | undefined, dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>, removeValues?: string[]): void => {
  let items: string[] | null | undefined = allValues;
  const {
    change: changeType,
    requestChange,
    onChange
  } = choice.change;
  switch (changeType) {
    case 'static': // nothing to do
    case 'controlled':
      // nothing to do
      return;
    case 'hybrid':
      {
        items = requestChange(allValues as DataKey[], choice.id, otherSelected);
        if (items == null) {
          return;
        }
        break;
      }
    case 'internal':
      {
        break;
      }
    default:
      assertNever(changeType);
      return;
  }
  if (removeValues == null) {
    const {
      values,
      others
    } = getValuesAndOthers(items, canHaveOthers);
    dispatch({
      type: 'multipleSet',
      id: choice.id,
      items: values,
      others
    });
  } else {
    dispatch({
      type: 'multipleRemove',
      id: choice.id,
      items: removeValues,
      others: removeValues.length === 0
    });
  }
  if (onChange != null) {
    onChange(items as DataKey[], choice.id, otherSelected);
  }
};
export const SelectChipsElement = props => {
  const $ = _c(40);
  const {
    type,
    useChips,
    choice,
    selected,
    selectedOthers,
    dispatch,
    t
  } = props;
  const {
    id,
    description,
    change
  } = choice;
  const label = choice.label ?? id;
  const limit = type === "values" || type === "valuesOf" || type === "metrics" ? choice.limit : undefined;
  const limitReached = limit != null && selected.length >= limit;
  let addOthers;
  let items;
  let options;
  let t0;
  if ($[0] !== id || $[1] !== label || $[2] !== limitReached || $[3] !== props || $[4] !== selected || $[5] !== t || $[6] !== type) {
    options = getChoiceOptions(props);
    addOthers = options.length - selected.length > 1 && (type === "values" || type === "valuesOf") && !limitReached;
    items = getItems({
      type,
      selected,
      limitReached,
      addOthers,
      options,
      label,
      id,
      t
    });
    t0 = label == null || label.length === 0 ? t("select") : `${t("select")} ${singleToPlural(label, options.length)}`;
    $[0] = id;
    $[1] = label;
    $[2] = limitReached;
    $[3] = props;
    $[4] = selected;
    $[5] = t;
    $[6] = type;
    $[7] = addOthers;
    $[8] = items;
    $[9] = options;
    $[10] = t0;
  } else {
    addOthers = $[7];
    items = $[8];
    options = $[9];
    t0 = $[10];
  }
  const title = t0;
  let t1;
  if ($[11] !== addOthers || $[12] !== choice || $[13] !== dispatch || $[14] !== options || $[15] !== props.otherSelected) {
    t1 = (_, items_0) => {
      const canHaveOthers = addOthers && options.length - items_0.length > 1;
      const allValues = items_0.map(_temp4);
      handleChange(choice, allValues, canHaveOthers, props.otherSelected, dispatch);
    };
    $[11] = addOthers;
    $[12] = choice;
    $[13] = dispatch;
    $[14] = options;
    $[15] = props.otherSelected;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  const onChange = t1;
  let t2;
  if ($[17] !== id || $[18] !== selected || $[19] !== selectedOthers) {
    t2 = selectedOthers === true ? [generateIdForSelectionOthers(id), ...selected] : selected;
    $[17] = id;
    $[18] = selected;
    $[19] = selectedOthers;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  const value_0 = t2;
  const t3 = `${title.length * 18}px`;
  const t4 = `${id}-add`;
  const t5 = items.length > 10;
  const t6 = limit == null || options.length <= limit;
  const t7 = selected.length > 0 ? "text" : "tonal";
  const t8 = change.description ?? description;
  let t9;
  if ($[21] !== id || $[22] !== items || $[23] !== label || $[24] !== t || $[25] !== t7 || $[26] !== t8 || $[27] !== useChips || $[28] !== value_0) {
    t9 = {
      options: items,
      value: value_0,
      variant: t7,
      useChips,
      choiceId: id,
      choiceLabel: label,
      choiceDescription: t8,
      t
    };
    $[21] = id;
    $[22] = items;
    $[23] = label;
    $[24] = t;
    $[25] = t7;
    $[26] = t8;
    $[27] = useChips;
    $[28] = value_0;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== items || $[31] !== onChange || $[32] !== t3 || $[33] !== t4 || $[34] !== t5 || $[35] !== t6 || $[36] !== t9 || $[37] !== title || $[38] !== value_0) {
    t10 = <MultiSelectDropdown buttonWidth={t3} key={t4} includeSearch={t5} title={title} buttonTitle={title} options={items} allowSelectAll={t6} value={value_0} onChange={onChange} CustomAnchorElement={SelectChipsButton} customAnchorElementProps={t9} fixedPositionOnOpen={true} />;
    $[30] = items;
    $[31] = onChange;
    $[32] = t3;
    $[33] = t4;
    $[34] = t5;
    $[35] = t6;
    $[36] = t9;
    $[37] = title;
    $[38] = value_0;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  return t10;
};
const StyledChipsGrid = styled(Grid, {
  shouldForwardProp: prop => prop !== 'editable'
})<{
  editable: boolean;
}>(({
  theme,
  editable
}) => ({
  padding: theme.spacing(0),
  display: 'flex',
  alignItems: 'center',
  backgroundColor: editable ? theme.tokens.background.main.pressed : undefined,
  flexWrap: 'wrap',
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(0),
  width: 'fit-content',
  borderRadius: theme.borderRadius.medium
}));
const createChipElement = (props: {
  chartType: ChartType;
  value: string;
  label?: string;
  color?: ThemeColor;
  pattern?: LinePattern;
  locked: boolean;
  removeHandler: (value: string) => void;
} & TooltipPropsWithAlert): ReactElement => {
  const {
    chartType,
    value,
    label,
    color,
    pattern,
    locked,
    removeHandler,
    ...tooltipProps
  } = props;
  return <ChartChip key={value} label={getLabelOrValueAsLabel(label, value)} color={color} pattern={pattern} chartType={chartType} onDelete={locked ? undefined : () => {
    removeHandler(value);
  }} {...tooltipProps} />;
};
const StyledTooltipBody = styled('div')(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));
const stringSort = (a: string, b: string): number => a.localeCompare(b);

// Unordered array comparison for arrays of primitives
const arraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort(stringSort);
  const sortedB = [...b].sort(stringSort);
  return sortedA.every((val, index) => val === sortedB[index]);
};
export default memo(MultipleSelection, (prevProps, nextProps) => arraysEqual(prevProps.selected, nextProps.selected) && prevProps.type === nextProps.type && prevProps.choice === nextProps.choice && prevProps.dispatch === nextProps.dispatch && prevProps.selectedOthers === nextProps.selectedOthers && prevProps.locked === true === (nextProps.locked === true) && (prevProps.type !== 'valuesOf' || prevProps.otherSelected === nextProps.otherSelected)) as typeof MultipleSelection;
function _temp(v_0) {
  return !isSelectionOthersId(v_0);
}
function _temp2(t0) {
  const {
    value: value_3
  } = t0;
  return !isSelectionOthersId(value_3);
}
function _temp3(t0) {
  const {
    value: value_4,
    label: label_1,
    alert: alert_1,
    color: color_1
  } = t0;
  return <StyledTooltipBody key={label_1}><Badge color={color_1 as BadgeColor} key="color" variant="lighter" />{getLabelOrValueAsLabel(label_1, value_4)}<AlertSymbol alert={alert_1} width="1.1em" height="1.1em" key="alert" /></StyledTooltipBody>;
}
function _temp4(t0) {
  const {
    value
  } = t0;
  return value;
}